import { useState, useEffect, useCallback } from 'react'
import {
  Box, Typography, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Button, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, InputAdornment, IconButton, Chip, Tooltip,
  Alert, Snackbar
} from '@mui/material'
import { Search, LockReset, ContentCopy, AdminPanelSettings, Edit } from '@mui/icons-material'
import { useAuth } from '@/contexts/AuthContext'
import { useNavigate } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { apiClient } from '@/services/apiClient'
import { AdminUser } from '@/types'

export default function AdminPortal() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()

  const [users, setUsers] = useState<AdminUser[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')

  const [resetDialogOpen, setResetDialogOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null)
  const [resetting, setResetting] = useState(false)
  const [resetLink, setResetLink] = useState<string | null>(null)
  const [copyAlertOpen, setCopyAlertOpen] = useState(false)

  const [editEmailDialogOpen, setEditEmailDialogOpen] = useState(false)
  const [editEmailUser, setEditEmailUser] = useState<AdminUser | null>(null)
  const [newEmail, setNewEmail] = useState('')
  const [savingEmail, setSavingEmail] = useState(false)

  const loadUsers = useCallback(async () => {
    try {
      const data = await apiClient.get<AdminUser[]>('/api/admin/users')
      setUsers(data)
    } catch {
      enqueueSnackbar('Failed to load users', { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }, [enqueueSnackbar])

  useEffect(() => {
    if (!user?.isAdmin) {
      navigate('/', { replace: true })
      return
    }
    loadUsers()
  }, [user, navigate, loadUsers])

  const filteredUsers = users.filter(u =>
    u.displayName.toLowerCase().includes(search.toLowerCase()) ||
    u.email.toLowerCase().includes(search.toLowerCase())
  )

  const openResetDialog = (adminUser: AdminUser) => {
    setSelectedUser(adminUser)
    setResetLink(null)
    setResetDialogOpen(true)
  }

  const closeResetDialog = () => {
    setResetDialogOpen(false)
    setSelectedUser(null)
    setResetLink(null)
  }

  const handleResetPassword = async () => {
    if (!selectedUser) return
    setResetting(true)
    try {
      const result = await apiClient.post<{ resetLink: string }>(
        `/api/admin/users/${selectedUser.userId}/reset-password`
      )
      setResetLink(result.resetLink)
    } catch (error) {
      enqueueSnackbar(
        error instanceof Error ? error.message : 'Failed to generate reset link',
        { variant: 'error' }
      )
    } finally {
      setResetting(false)
    }
  }

  const copyResetLink = async () => {
    if (!resetLink) return
    await navigator.clipboard.writeText(resetLink)
    setCopyAlertOpen(true)
  }

  const openEditEmailDialog = (adminUser: AdminUser) => {
    setEditEmailUser(adminUser)
    setNewEmail(adminUser.email)
    setEditEmailDialogOpen(true)
  }

  const closeEditEmailDialog = () => {
    setEditEmailDialogOpen(false)
    setEditEmailUser(null)
    setNewEmail('')
  }

  const handleSaveEmail = async () => {
    if (!editEmailUser) return
    const trimmedEmail = newEmail.trim()
    setSavingEmail(true)
    try {
      const updated = await apiClient.post<AdminUser>(
        `/api/admin/users/${editEmailUser.userId}/email`,
        { newEmail: trimmedEmail }
      )
      setUsers(prev => prev.map(u => u.userId === updated.userId ? updated : u))
      enqueueSnackbar('Email updated successfully', { variant: 'success' })
      closeEditEmailDialog()
    } catch (error) {
      enqueueSnackbar(
        error instanceof Error ? error.message : 'Failed to update email',
        { variant: 'error' }
      )
    } finally {
      setSavingEmail(false)
    }
  }

  if (!user?.isAdmin) {
    return null
  }

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <AdminPanelSettings color="primary" sx={{ fontSize: 32 }} />
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Admin Portal
        </Typography>
      </Box>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Users ({users.length})
        </Typography>

        <TextField
          size="small"
          placeholder="Search by name or email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 2, width: { xs: '100%', sm: 320 } }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                      No users found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredUsers.map((u) => (
                    <TableRow key={u.userId} hover>
                      <TableCell>{u.displayName}</TableCell>
                      <TableCell>{u.email}</TableCell>
                      <TableCell>
                        {u.isAdmin && (
                          <Chip label="Admin" size="small" color="primary" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                            gap: 0.5,
                            flexWrap: 'nowrap',
                          }}
                        >
                          <Tooltip title="Edit email">
                            <IconButton
                              size="small"
                              onClick={() => openEditEmailDialog(u)}
                              aria-label={`Edit email for ${u.displayName}`}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reset password">
                            <IconButton
                              size="small"
                              onClick={() => openResetDialog(u)}
                              aria-label={`Reset password for ${u.displayName}`}
                            >
                              <LockReset fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Reset Password Dialog */}
      <Dialog open={resetDialogOpen} onClose={closeResetDialog} fullWidth maxWidth="sm">
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent>
          {!resetLink ? (
            <Typography>
              Generate a password reset link for{' '}
              <strong>{selectedUser?.displayName}</strong> ({selectedUser?.email})?
              <br />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                The link will allow the user to set a new password and expires after use.
              </Typography>
            </Typography>
          ) : (
            <Box>
              <Alert severity="success" sx={{ mb: 2 }}>
                Password reset link generated. Share this link with the user.
              </Alert>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  wordBreak: 'break-all',
                  fontFamily: 'monospace',
                  fontSize: '0.8rem',
                  bgcolor: 'action.hover',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                }}
              >
                <Box sx={{ flex: 1 }}>{resetLink}</Box>
                <Tooltip title="Copy to clipboard">
                  <IconButton size="small" onClick={copyResetLink} aria-label="Copy reset link">
                    <ContentCopy fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeResetDialog}>
            {resetLink ? 'Close' : 'Cancel'}
          </Button>
          {!resetLink && (
            <Button
              variant="contained"
              color="warning"
              onClick={handleResetPassword}
              disabled={resetting}
              startIcon={resetting ? <CircularProgress size={16} /> : <LockReset />}
            >
              {resetting ? 'Generating…' : 'Generate Reset Link'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Edit Email Dialog */}
      <Dialog open={editEmailDialogOpen} onClose={closeEditEmailDialog} fullWidth maxWidth="sm">
        <DialogTitle>Edit Email</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Update email address for <strong>{editEmailUser?.displayName}</strong>.
          </Typography>
          <TextField
            autoFocus
            label="Email address"
            type="email"
            fullWidth
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            disabled={savingEmail}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditEmailDialog} disabled={savingEmail}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveEmail}
            disabled={savingEmail || !newEmail.trim() || newEmail.trim() === editEmailUser?.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail.trim())}
            startIcon={savingEmail ? <CircularProgress size={16} /> : <Edit />}
          >
            {savingEmail ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={copyAlertOpen}
        autoHideDuration={2000}
        onClose={() => setCopyAlertOpen(false)}
        message="Reset link copied to clipboard"
      />
    </Box>
  )
}
